var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Modal from '@rad/components/ui/modal';
import TypeAhead from '@rad/components/inputs/type-ahead';
import { createParcel, findNearbyParcels } from '../../../common/network/parcels';
import { getParcelKinds } from '../../../common/network/parcel-kind';
import { onSubdomain } from '../../../common/routes/general';
import Dropdown from '@rad/components/inputs/dropdown';
import ActionButton from '@rad/components/ui/action-button';
import { useGeocoding } from './arcgis-map/hooks/useGeocoding';
import { getDistrictFromPoint, getAllDistricts } from '../../../common/network/district';
import { ArcGISMapLegacyHelper } from './arcgis-map/components/arcgis-map';
import { createDisplayConfig, regridParcelLayerURL } from './arcgis-map/constants';
import { useFetch, usePromise } from '@rad/hooks';
import { emptyProperty, propertyFromCreateResult } from '../../../types';
export const AddAddressModal = ({ isOpen, onClose, onParcelCreated, onExistingSelected, initialQuery = '', }) => {
    var _a;
    const { searchAddress } = useGeocoding();
    const [selectedAddress, setSelectedAddress] = React.useState(undefined);
    const [geocodeQuery, setGeocodeQuery] = React.useState(initialQuery);
    const [geocodeOptionsPromise, setGeocodeOptionsPromise] = React.useState(Promise.resolve([]));
    const [geocodeCanSubmit, setGeocodeCanSubmit] = React.useState(false);
    const [mapCenter, setMapCenter] = React.useState(undefined);
    const [districtMessage, setDistrictMessage] = React.useState(null);
    const [pointToCheck, setPointToCheck] = React.useState(null);
    const [selectedKind, setSelectedKind] = React.useState('');
    const lastCheckedPoint = React.useRef(null);
    const [parcelKindsPromise] = useFetch(onSubdomain
        ? getParcelKinds()
        : (_c, m) => m({ 200: () => __awaiter(void 0, void 0, void 0, function* () { return []; }) })[200](new Response()));
    const parcelKinds = usePromise(parcelKindsPromise);
    React.useEffect(() => {
        if ((parcelKinds === null || parcelKinds === void 0 ? void 0 : parcelKinds.length) && !selectedKind) {
            setSelectedKind(parcelKinds[0].name);
        }
    }, [parcelKinds]);
    const [pointDistrictPromise, reloadPointDistrict] = useFetch(getDistrictFromPoint(pointToCheck || [0, 0]));
    const [allDistrictsPromise] = useFetch(getAllDistricts);
    const allDistricts = usePromise(allDistrictsPromise);
    React.useEffect(() => {
        if (initialQuery) {
            setGeocodeQuery(initialQuery);
            setSelectedAddress({
                address: initialQuery,
                location: [0, 0],
                score: 0,
                attributes: {}
            });
        }
    }, [initialQuery]);
    React.useEffect(() => {
        const fetchResults = () => __awaiter(void 0, void 0, void 0, function* () {
            const promise = searchAddress(geocodeQuery);
            setGeocodeOptionsPromise(promise);
        });
        const timeout = setTimeout(fetchResults, 500);
        return () => clearTimeout(timeout);
    }, [geocodeQuery, searchAddress]);
    React.useEffect(() => {
        if (pointToCheck && pointToCheck !== lastCheckedPoint.current) {
            lastCheckedPoint.current = pointToCheck;
            reloadPointDistrict();
        }
    }, [pointToCheck]);
    React.useEffect(() => {
        if (pointToCheck) {
            pointDistrictPromise
                .then(pointDistrictArray => {
                const pointDistrict = pointDistrictArray[0];
                const message = (pointDistrict === null || pointDistrict === void 0 ? void 0 : pointDistrict.id)
                    ? `This address falls within the ${pointDistrict.title}. Once submitted, it will be reviewed by that district's administrators.`
                    : 'This address is not currently located within any district. You can still submit it, and our team will review it.';
                setDistrictMessage(message);
                setGeocodeCanSubmit(true);
            });
        }
    }, [pointDistrictPromise, pointToCheck]);
    const [candidates, setCandidates] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const buildCreateData = () => {
        if (!selectedAddress)
            return null;
        return {
            point: selectedAddress.location,
            address: selectedAddress.address,
            kind: selectedKind,
        };
    };
    const handleClose = () => {
        setCandidates(null);
        onClose();
    };
    const addParcel = () => __awaiter(void 0, void 0, void 0, function* () {
        const createData = buildCreateData();
        if (!createData)
            return;
        setIsSubmitting(true);
        try {
            if (candidates === null) {
                const nearby = yield findNearbyParcels(createData.point)();
                if (nearby.length > 0) {
                    setCandidates(nearby);
                    return;
                }
            }
            const res = yield createParcel(createData)();
            onParcelCreated(propertyFromCreateResult(res));
            handleClose();
        }
        finally {
            setIsSubmitting(false);
        }
    });
    const handleUseExisting = (parcel) => {
        onExistingSelected === null || onExistingSelected === void 0 ? void 0 : onExistingSelected(parcel);
        handleClose();
    };
    return (_jsx(Modal, { isOpen: isOpen, title: candidates ? 'Possible Existing Parcels' : 'Add New Address', onClose: handleClose, footer: candidates ? (_jsxs("div", { className: "d-flex gap-2 justify-content-end", children: [_jsx("button", { className: "btn btn-outline-secondary", onClick: () => setCandidates(null), disabled: isSubmitting, children: "Back" }), _jsx(ActionButton, { onClick: addParcel, className: "btn btn-primary", loadingText: "Adding...", children: "Add anyway" })] })) : (_jsx(ActionButton, { onClick: addParcel, className: "btn btn-primary", disabled: !geocodeCanSubmit || isSubmitting, loadingText: "Submitting...", children: "Confirm" })), children: candidates ? (_jsxs("div", { children: [_jsxs("p", { className: "mb-3", children: ["We found ", candidates.length === 1 ? 'an existing parcel' : `${candidates.length} existing parcels`, " near this location. Did you mean to use ", candidates.length === 1 ? 'it' : 'one of these', "?"] }), candidates.map(parcel => (_jsxs("div", { className: "d-flex justify-content-between align-items-center border rounded p-2 mb-2", children: [_jsxs("div", { children: [_jsx("div", { className: "fw-semibold", children: parcel.address }), parcel.kind && (_jsx("div", { className: "small text-muted", children: parcel.kind.displayName }))] }), onExistingSelected && (_jsx("button", { className: "btn btn-sm btn-primary", onClick: () => handleUseExisting(parcel), disabled: isSubmitting, children: "Use this one" }))] }, parcel.id)))] })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-sm mb-3", children: "Search for a US address using the geocoding service:" }), _jsx(TypeAhead, { label: "Address", value: selectedAddress, options: geocodeOptionsPromise, optionKey: option => option.address, optionLabel: option => option.address, placeholder: "Enter street address, city, state", loadingRender: "Loading...", filterOptions: options => options, onChange: input => {
                        setSelectedAddress(input);
                        setMapCenter(input.location);
                        setPointToCheck(input.location);
                    }, allowFreeTyping: true, onFreeType: input => {
                        setGeocodeQuery(input);
                        setGeocodeCanSubmit(false);
                        setPointToCheck(null);
                        setDistrictMessage(null);
                    } }), _jsx("div", { className: "mt-3", children: _jsx(Dropdown, { label: "Property Type", options: parcelKindsPromise, optionKey: (pk) => pk.name, optionLabel: (pk) => pk.displayName, value: selectedKind, onChange: (name) => setSelectedKind(name || '') }) }), mapCenter && selectedAddress && (_jsxs(_Fragment, { children: [_jsx("div", { className: "mt-3", style: { height: '400px', width: '100%' }, children: _jsx(ArcGISMapLegacyHelper, { config: createDisplayConfig({
                                    center: mapCenter,
                                    zoom: 15,
                                    height: '100%',
                                    width: '100%',
                                    showBasemapSelector: false,
                                    uiComponents: [],
                                }), layerData: {
                                    'items': [Object.assign({}, ((x) => (Object.assign(Object.assign({}, x), { 'parcel': Object.assign(Object.assign({}, x.parcel), { 'point': selectedAddress.location, 'address': selectedAddress.address }) })))(emptyProperty('temp-property', 'temp-parcel')))],
                                    'areas': (_a = allDistricts === null || allDistricts === void 0 ? void 0 : allDistricts.flatMap(district => district.area ? [district.area] : [])) !== null && _a !== void 0 ? _a : [],
                                }, tileLayers: [{
                                        url: regridParcelLayerURL,
                                        opacity: 0.6,
                                        minScale: 25000,
                                        maxScale: 0,
                                        visible: true,
                                        displayName: "Parcel Borders",
                                        showToggle: false,
                                    }] }) }), districtMessage && (_jsx("div", { className: "mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm", children: districtMessage }))] }))] })) }));
};
